import { useRef } from 'react';
import { type SearchModuleTranslationKey } from '@pkgs/components/types/typedTranslationKeys';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { Modal, type ModalState } from '@ev-tech/ui-core-components/Modal';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { CrossNakedIcon as CrossNaked } from '@ev-tech/search-modules-shared/Icons';

import { Headline } from '../Headline/Headline';

import styles from './PreferencesDetailsModal.module.css';

type PreferencesDetailsModalProps = {
  modalState: ModalState;
};

export const PreferencesDetailsModal = ({ modalState }: PreferencesDetailsModalProps) => {
  const { t } = useAppContext();
  const { isOpen, setOpen, close } = modalState;

  const SCORE_DETAIL_TRANSLATION_PREFIX = 'search.aiSearch.filtersDropdown.tooltipInfo.score.';
  const scoreDetails: {
    score: string;
    translation: Extract<SearchModuleTranslationKey, `${typeof SCORE_DETAIL_TRANSLATION_PREFIX}${string}`>;
  }[] = [
    {
      score: 'excellent',
      translation: `${SCORE_DETAIL_TRANSLATION_PREFIX}excellent`,
    },
    {
      score: 'veryGood',
      translation: `${SCORE_DETAIL_TRANSLATION_PREFIX}veryGood`,
    },
    {
      score: 'good',
      translation: `${SCORE_DETAIL_TRANSLATION_PREFIX}good`,
    },
    {
      score: 'other',
      translation: `${SCORE_DETAIL_TRANSLATION_PREFIX}other`,
    },
  ];

  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Modal
      modal
      open={isOpen}
      onOpenChange={(open, eventDetails) => {
        if (!open) eventDetails.event.stopPropagation();
        setOpen(open);
      }}
      aria-label={t('search.aiSearch.scoresDropdown.title')}
      data-testid="search-components_preferences-details-modal"
      className={styles.preferencesDetailsModal}
    >
      <div className={styles.preferencesDetailsModalHeader}>
        <Headline size="h5" label={t('search.aiSearch.scoresDropdown.title')} />
        <IconButton
          as="button"
          className={styles.iconButton}
          size="small"
          ref={closeButtonRef}
          icon={<CrossNaked />}
          variant="tertiary"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            e.preventDefault();
            close();
          }}
          label="Close"
        />
      </div>
      <div className={styles.preferencesDetailsModalContent}>
        <Copy text={t('search.aiSearch.filtersDropdown.tooltipInfo')} size="l" />
        <Copy text={t('search.aiSearch.filtersDropdown.tooltipInfo.scoreTopic')} size="l" />
      </div>
      <ul className={styles.preferencesDetailsModalList}>
        {scoreDetails.map((scoreEntry) => (
          <li key={scoreEntry.score}>
            <Copy text={t(scoreEntry.translation)} size="l" />
          </li>
        ))}
      </ul>
      <Copy text={t('search.aiSearch.filtersDropdown.tooltipInfo.resultsSortedByAverage')} size="l" />
    </Modal>
  );
};
